<template>
  <module-page v-bind="pageParams" :customBreadcrumbs="customBreadcrumbs">
    <v-tabs class="page__tabs" hide-slider>
      <v-tab
        :key="i"
        v-for="(contract, i) in allowedContracts"
        active-class="financial--background white--text opaque"
        @click="getStatus(contract)"
      >
        <span style="width: max-content">{{ getContractTitle(contract) }}</span>
      </v-tab>
    </v-tabs>

    <div class="financial__base-date" v-if="financialPositionData.baseDate">
      <div>
        {{ $t('pages.financialPosition.baseDate') }}:
        {{ formatDate(financialPositionData.baseDate) }}
        <span v-if="!mobile" class="financial__base-date__description">
          {{ pageParams.texts.baseDateDescription }}
        </span>
      </div>

      <button
        @click="print"
        class="financial__base-date__print-btn financial--background white--text no-print"
      >
        <i class="bexsicon-printer icon"></i>
      </button>
    </div>

    <div v-if="mobile" class="financial__base-date__description">
      {{ pageParams.texts.baseDateDescription }}
    </div>

    <div v-if="financialPositionError" class="empty-state">
      {{ $t('responses.error') }}
    </div>

    <div v-else>
      <v-card class="financial__status-iframe">
        <iframe
          v-if="integrations.JUNIX_STATUS_URL"
          :src="`${integrations.JUNIX_STATUS_URL}?numeroContrato=${selectedContract.id}`"
          class="financial__status-iframe__iframe"
        ></iframe>
      </v-card>

      <v-card class="mt-4">
        <v-card-title class="topic__title financial--color">
          {{ $t('pages.financialPosition.financialResume') }}
        </v-card-title>

        <v-card-text class="topic__body">
          <screen-loading
            v-if="loadingTotals"
            margin="20px 25%"
          ></screen-loading>

          <v-layout v-else row wrap>
            <v-flex class="financial__totals" xs12 md4 lg4>
              <screen-loading
                v-if="financialPositionData.paidTotal == null"
                margin="20px 25%"
              ></screen-loading>

              <div v-else class="financial__totals__content">
                {{ $t('pages.financialPosition.totalPaid') }}:
                <div class="financial__totals__value financial--color">
                  {{ formatMoney(financialPositionData.paidTotal) }}
                </div>

                <div
                  v-if="texts.financialNominalValue"
                  class="financial__totals__notation"
                >
                  {{ texts.financialNominalValue }}
                </div>
              </div>
            </v-flex>

            <v-flex class="financial__totals" xs12 md4 lg4>
              <screen-loading
                v-if="financialPositionData.toExpireTotal == null"
                margin="20px 25%"
              ></screen-loading>

              <div v-else class="financial__totals__content">
                {{ $t('pages.financialPosition.toExpire') }}:
                <div class="financial__totals__value">
                  {{ formatMoney(financialPositionData.toExpireTotal) }}
                </div>
              </div>
            </v-flex>

            <v-flex class="financial__totals" xs12 md4 lg4>
              <screen-loading
                v-if="financialPositionData.lateTotal == null"
                margin="20px 25%"
              ></screen-loading>

              <div v-else class="financial__totals__content">
                {{ $t('pages.financialPosition.late') }}:
                <div class="financial__totals__value error--text">
                  {{ formatMoney(financialPositionData.lateTotal) }}
                </div>
              </div>
            </v-flex>
          </v-layout>
        </v-card-text>
      </v-card>

      <financial-card-table
        :title="$t('pages.financialPosition.lateValues')"
        :headers="lateHeaders"
        :items="financialPositionData.late"
        :unitFinancialPositions="unitFinancialPositions"
        mode="late"
        :loading="loadingLate"
        :no-data-text="pageParams.emptyState.late"
        :expand-column="expandColumn"
        :slip-details-headers="slipDetailsHeaders"
      >
        <template #title="{ title }">
          <v-card-title class="topic__title error--text">
            {{ title }}
          </v-card-title>
        </template>

        <template #warnings>
          <div
            class="payment-slip__late"
            v-if="financialPositionData.late.length > 0"
          >
            <router-link
              v-if="financialPositionData.hasPayableLatePaymentSlips"
              :to="{ name: 'latePaymentSlip' }"
              tag="span"
              class="payment-slip__late__info late"
            >
              <i class="payment-slip__late__icon bexsicon-alert icon"></i>

              <span>
                Você possui parcela(s) em atraso.
                <b>
                  Para gerar o(s) boleto(s),
                  <span style="text-decoration: underline">clique aqui.</span>
                </b>
              </span>
            </router-link>

            <router-link
              v-else
              :to="{ name: 'ticketForm' }"
              tag="span"
              class="payment-slip__late__info late"
            >
              <i class="payment-slip__late__icon bexsicon-alert icon"></i>

              <span>
                {{ $t('pages.financialPosition.notPayableLateMessage') }}
                <span
                  v-if="ticketCrmEnabled"
                  style="text-decoration: underline; font-weight: bold"
                >
                  {{ $t('pages.paymentSlip.error.link') }}
                </span>
              </span>
            </router-link>
          </div>
        </template>

        <template v-slot:col-expirationDate="props">
          <span v-if="!(props.item.contractQty >= 2)">
            {{ formatUtcDate(props.item.expirationDate) }}
          </span>
          <span v-else class="bold--text">
            <!-- Showing current solicitation expiration date -->
            {{
              props.item.releaseDate
                ? formatUtcDate(props.item.releaseDate)
                : ''
            }}
          </span>
        </template>
        <template #col-noChargeValue="props">
          <span>
            {{
              !(props.item.contractQty >= 2)
                ? formatMoney(props.item.noChargeValue)
                : ''
            }}
          </span>
        </template>
        <template #col-costs="props">
          {{
            !(props.item.contractQty >= 2) ? formatMoney(props.item.costs) : ''
          }}
        </template>
        <!-- <template #col-discountedValue="props">
          <span class="financial__discounted-value">
            {{
              formatMoney(
                !(props.item.contractQty >= 2)
                  ? props.item.discountedValue
                  : props.item[expandColumn].total
              )
            }}
          </span>
        </template> -->
        <template #expandcol-contractTitle="expandProps">
          <span class="financial__discounted-value">
            {{ getContractTitle(expandProps.item) }}
          </span>
        </template>
      </financial-card-table>

      <ToExpireTable
        :financialPosition="financialPositionData"
        :items="financialPositionData.toExpire"
        :unitFinancialPositions="unitFinancialPositions"
        :loading="loadingToExpire"
        :expand-column="expandColumn"
        :slip-details-headers="slipDetailsHeaders"
        :email="email"
        :cellphone="cellphone"
        :debitBlock="debitBlock"
      >
      </ToExpireTable>

      <v-card class="mt-4 mb-5 financial-card">
        <v-card-title class="topic__title financial--color">
          {{ $t('pages.financialPosition.paidValues') }}
        </v-card-title>

        <v-card-text class="topic__body">
          <screen-loading v-if="loadingPaid" margin="20px 25%"></screen-loading>

          <v-data-table
            v-else
            :headers="paidHeaders"
            :items="financialPositionData.paid"
            hide-actions
            :no-data-text="pageParams.emptyState.paid"
            sort-icon="bexsicon-down"
          >
            <template #items="props">
              <td
                v-for="(header, i) in paidHeaders"
                :key="i"
                :class="`text-xs-center ${
                  header.value === 'total' ? 'financial--color bold--text' : ''
                }`"
              >
                {{ header.formatter(props.item[header.value]) }}
              </td>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </div>
  </module-page>
</template>

<script>
import bus from '@/utils/bus';
import enums from '../../utils/enums';
import ModulePage from '@/components/layout/Page';
import ScreenLoading from '@/components/loaders/ScreenLoading';
import toExpirePaymentSlipMixin from '@/mixins/modules/toExpirePaymentSlip';
import notificationTypes from '@/utils/notificationTypes';
import getSlipUrl from '@/services/getSlipUrl';
import logTrace from '@/services/logTrace';
import FinancialCardTable from '../../components/financial/FinancialCardTable.vue';
import ToExpireTable from '../../components/financial/ToExpireTable.vue';
import PaymentButton from '@/components/financial/PaymentButton';
import {
  APP,
  TICKET_CRM,
  CONFIG,
  NOTIFICATIONS,
  USER,
  FEATURE_TOGGLES,
  FINANCIAL_POSITION,
} from '../../utils/constants';

export default {
  name: 'financial-status',
  components: {
    ModulePage,
    ScreenLoading,
    PaymentButton,
    FinancialCardTable,
    ToExpireTable,
  },
  mixins: [toExpirePaymentSlipMixin], //, financialPositionMixin
  computed: {
    unitFinancialPositions() {
      return this.$store.getters[FINANCIAL_POSITION.GET_UNIT_POSITIONS];
    },
    financialPositionData() {
      return this.$store.getters[FINANCIAL_POSITION.GET_DATA];
    },
    financialPositionError() {
      return this.$store.getters[FINANCIAL_POSITION.ERROR];
    },
    loadingPaid() {
      return this.$store.getters[FINANCIAL_POSITION.LOADING_PAID];
    },
    loadingLate() {
      return this.$store.getters[FINANCIAL_POSITION.LOADING_LATE];
    },
    loadingToExpire() {
      return this.$store.getters[FINANCIAL_POSITION.LOADING_TO_EXPIRE];
    },
    loadingTotals() {
      return this.$store.getters[FINANCIAL_POSITION.LOADING_TOTALS];
    },

    ticketCrmEnabled() {
      return this.$store.getters[TICKET_CRM.GET_ENABLED];
    },
    customBreadcrumbs() {
      const { title } = this.pageParams;
      const text = `${title}: ${this.getContractTitle(this.selectedContract)}`;
      return [{ text }];
    },
    lateHeaders() {
      return this.visibleHeaders(this.lateHeadersConfig);
    },
    toExpireHeaders() {
      return this.visibleHeaders(this.toExpireHeadersConfig);
    },
    paidHeaders() {
      return this.visibleHeaders(this.paidHeadersConfig);
    },
    slipDetailsHeaders() {
      return this.visibleHeaders(this.slipDetailsHeadersConfig);
    },
    texts() {
      return this.$store.getters[CONFIG.GET].texts || {};
    },
    integrations() {
      return this.$store.getters[CONFIG.GET_SHARED_INTEGRATIONS];
    },
    allowedContracts() {
      const { FINANCIAL_POSITION } = enums.financialModules;
      return this.getAllowedContracts(
        FINANCIAL_POSITION,
        this.selectedUnit.contracts
      );
    },
  },
  data() {
    return {
      pageParams: this.$store.getters[APP.PAGES].financialPosition,
      currentContract: null,
      expandColumn: 'slipsUnion',
      lateHeadersConfig: [
        {
          text: ' ',
          value: 'slipsUnion',
          align: 'left',
          class: 'no-print',
          sortable: false,
          width: '40px',
        },
        {
          text: 'Vencimento',
          formatter: this.formatUtcDate,
          value: 'expirationDate',
          align: 'center',
        },
        {
          text: 'Valor',
          value: 'noChargeValue',
          formatter: this.formatMoney,
          align: 'center',
          showMobile: false,
        },
        {
          text: 'Encargos',
          value: 'costs',
          formatter: this.formatMoney,
          align: 'center',
          showMobile: false,
        },
        {
          text: 'Valor Atualizado',
          formatter: (v, item, headerValue, extraInfo) =>
            extraInfo == 'slipsUnion'
              ? this.formatMoney(v, item, headerValue, extraInfo)
              : this.formatUnifiedValue(v, item, headerValue, extraInfo),
          itemClass: 'error--text bold--text text-xs-center',
          value: 'discountedValue',
          align: 'center',
        },
      ],
      // toExpireHeadersConfig: [
      //   {
      //     text: ' ',
      //     value: 'slipsUnion',
      //     align: 'left',
      //     class: 'no-print',
      //     sortable: false,
      //     width: '40px',
      //   },
      //   {
      //     text: 'Vencimento',
      //     value: 'expirationDate',
      //     formatter: this.formatUtcDate,
      //     align: 'center',
      //   },
      //   {
      //     text: 'Valor',
      //     value: 'discountedValue',
      //     formatter: this.formatMoney,
      //     align: 'center',
      //     itemClass: 'text-xs-center bold--text',
      //   },
      //   {
      //     text: ' ',
      //     value: 'action',
      //     align: 'center',
      //     class: 'no-print',
      //     itemClass: 'text-xs-center third-fit-in-mobile',
      //   },
      // ],
      paidHeadersConfig: [
        {
          text: 'Vencimento',
          formatter: this.formatUtcDate,
          value: 'expirationDate',
          align: 'center',
        },
        {
          text: 'Pagamento',
          formatter: this.formatUtcDate,
          value: 'paymentDate',
          align: 'center',
          showMobile: false,
        },
        {
          text: 'Valor',
          formatter: this.formatMoney,
          value: 'discountedValue',
          align: 'center',
          showMobile: false,
        },
        {
          text: 'Encargos',
          formatter: this.formatMoney,
          value: 'costs',
          align: 'center',
          showMobile: false,
        },
        {
          text: 'Descontos',
          formatter: this.formatMoney,
          value: 'discount',
          align: 'center',
          showMobile: false,
        },
        {
          text: 'Total',
          formatter: this.formatMoney,
          value: 'total',
          align: 'center',
        },
      ],
      slipDetailsHeadersConfig: [
        {
          text: 'Contrato',
          value: 'type',
          align: 'center',
          showMobile: false,
        },
        {
          text: 'Descrição',
          value: 'contractTitle',
          formatter: (v, item) => this.getContractTitle(item),
          align: 'center',
        },
        {
          text: 'Total',
          value: 'partialValue',
          align: 'center',
          formatter: this.formatMoney,
        },
      ],
      hasPayableLatePaymentSlips: false,
      activeStatus: 'Ativo',
      email: '',
      cellphone: {
        countryCode: '+55',
        areaCode: '',
        number: '',
      },
    };
  },
  created() {
    bus.$on('unit:change', this.updateStatus);
    bus.$on('unit:update', this.updateStatus);

    this.email = this.user.email;
    if (this.imobmeUser && this.imobmeUser.cellphone) {
      const { number, areaCode, countryCode } = this.imobmeUser.cellphone;
      this.cellphone = { number, areaCode, countryCode: countryCode || '+55' };
    }

    this.getStatus();
  },
  beforeDestroy() {
    bus.$off('unit:change', this.updateStatus);
    bus.$off('unit:update', this.updateStatus);
  },
  methods: {
    visibleHeaders(headers, mobile = this.mobile) {
      return headers.filter((header) =>
        mobile ? header.showMobile !== false : header.showDesktop !== false
      );
    },

    updateStatus(unitObj = null) {
      this.getStatus();
    },

    async getStatus(contract = null) {
      if (!contract)
        contract = this.selectedUnit.contracts.find((o) => o.isValid);

      this.currentContract = contract;

      const tracePath = this.enums.logTraceActionRoutes['financialPosition'];
      if (tracePath) logTrace({ action: tracePath, contractId: contract.id });

      this.$store.dispatch(FINANCIAL_POSITION.FETCH_POSITION, { contract });
    },

    print() {
      this.createTrace(
        this.enums.logTraceActions.financialPositionPrint,
        {},
        this.currentContract.id
      );
      window.print();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/breakpoint';

.third-fit-in-mobile {
  padding: 0 !important;
}

@media print {
  >>> .financial-card {
    width: 100%;
  }

  >>> .v-card {
    padding: 0;
    margin: 0;

    .v-card__title {
      padding: 0.5em 0.5em 0;
    }

    .v-card__text {
      padding: 0;
    }
  }

  >>> .v-table {
    padding: 0;
    margin: 0;
  }

  .financial__status-iframe {
    height: 75px;

    &__iframe {
      height: 75px !important;
      padding: 0 !important;
    }
  }
}

>>> .financial-card {
  .expanded-area {
    background: #eafbe4; /* Um verde bem claro de fundo, combinando com #4bb629 */
    border-left: 4px solid #4bb629; /* Destaque forte à esquerda */
    border-radius: 4px;
    margin: 8px 0;
    padding: 16px;
    transition: background 0.3s, border 0.3s;
  }

  .v-btn-toggle {
    box-shadow: none !important;
    background-color: inherit !important;

    .v-btn--active {
      &::before {
        background-color: transparent !important;
      }
      &:hover::before {
        background-color: black !important;
      }
    }
  }
}

.payment-slip {
  &__late {
    background: var(--error-color);
    border-radius: 4px;
    color: white !important;
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    text-align: center;
    cursor: pointer;
    padding: 6px 0;
    width: 99%;
    margin: auto;
    margin: 10px auto 15px;

    &__info {
      font-size: 12px;
      flex: 1;
      align-self: center;
      margin-right: 2px;

      span {
        font-weight: bold;
      }
    }

    &__icon {
      margin-top: 3px;
      margin-bottom: auto;
      margin-right: 7px;
      padding: 0;
      flex: 0.08;
    }
  }
}

.financial {
  &__base-date {
    display: flex;
    justify-content: space-between;
    margin: 10px 0;
    font-size: 12px;
    padding: 0 5px;
    color: var(--light-dark-color);

    @include breakpoint(mobile) {
      font-size: 14px;
    }

    &__print-btn {
      min-width: 40px;
      max-height: 28px;
      border-radius: 5px;
      padding: 5px 0 2px 0;

      @include breakpoint(mobile) {
        max-height: 25px;
        padding: 3px 0 2px 0;
      }
    }

    &__description {
      padding: 5px 12px;
      font-size: 10px;
      color: var(--label-color);

      @include breakpoint(mobile) {
        padding: 5px;
        width: 90%;
        margin-top: -16px;
      }
    }
  }

  >>> &__pay-btn {
    flex: 0.2;
    z-index: 1;
    height: 25px;
    min-width: 60px;
    font-size: 12px;
    margin: 0;
    padding: 5px 14px !important;
    border-radius: 5px;
    text-decoration: none;
    color: #fff;
    text-align: center;
    line-height: 25px;
  }

  &__status-iframe {
    &__iframe {
      width: 100%;
      border: none;
      height: 91px;
      padding: 1em;

      @include breakpoint(mobile) {
        height: 115px;
      }
    }
  }

  &__totals {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    text-align: center;
    background-color: #f1eeee;
    padding: 1em 1.2em;
    height: 60px;
    border: 5px solid #fff;

    &__value {
      display: inline;
      padding: 0 0.2em;
      font-weight: 700;
      color: var(--light-dark-color);
    }

    &__notation {
      left: 37.5%;
      font-size: 0.75em;
    }

    @media print {
      -ms-flex-preferred-size: 33.33333333333333% !important;
      flex-basis: 33.33333333333333% !important;
      -webkit-box-flex: 0 !important;
      -ms-flex-positive: 0 !important;
      flex-grow: 0 !important;
      max-width: 33.33333333333333% !important;
    }
  }
}
</style>



// WEBPACK FOOTER //
// ./src/views/financial/FinancialPosition.vue